/**
 * usePunch Hook - Offline-First Punch Operations with Anti-Fraud
 *
 * Flow:
 * 1. User taps punch button
 * 2. Anti-fraud validation runs (GPS accuracy, device binding, velocity)
 * 3. Event saved to IndexedDB immediately (optimistic) with fraud flags
 * 4. UI updates instantly (optimistic)
 * 5. SyncService picks up event and sends to server
 * 6. Server performs additional validation
 * 7. On success: marked as synced
 * 8. On failure: marked as error, retries later
 *
 * User sees instant feedback, network happens in background.
 * Fraud flags are stored with the event for server-side review.
 */
import { useState, useCallback } from 'react';
import { createPunchEvent, BreakReason, WorkMode } from '../db/database';
import { syncService } from '../services/syncService';
import { useAppStore } from '../store/useAppStore';
import { validatePunch, bindDeviceToUser, PunchValidation } from '../services/antiFraud';

// =============================================================================
// Types
// =============================================================================

interface LocationData {
  latitude?: number;
  longitude?: number;
  accuracy?: number;
}

interface PunchOptions {
  breakReason?: BreakReason;
  worksiteUuid?: string;
  qrToken?: string;
  workMode?: WorkMode;
}

interface UsePunchReturn {
  // State
  isLoading: boolean;
  error: string | null;
  lastValidation: PunchValidation | null;

  // Actions
  punchIn: (location?: LocationData, worksiteUuid?: string, qrToken?: string, workMode?: WorkMode) => Promise<void>;
  punchOut: (location?: LocationData) => Promise<void>;
  startBreak: (reason: BreakReason, location?: LocationData) => Promise<void>;
  endBreak: (location?: LocationData) => Promise<void>;

  // Location helper
  getCurrentLocation: () => Promise<LocationData | null>;

  // Device binding
  bindDevice: (userId: number) => Promise<void>;
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function usePunch(): UsePunchReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastValidation, setLastValidation] = useState<PunchValidation | null>(null);

  const {
    user: usuarioActual,
    optimisticPunchIn,
    optimisticPunchOut,
    optimisticBreakStart,
    optimisticBreakEnd,
    showToast,
  } = useAppStore();

  /**
   * Get current GPS location with timeout
   */
  const getCurrentLocation = useCallback(async (): Promise<LocationData | null> => {
    if (!navigator.geolocation) {
      console.warn('[Punch] Geolocation not supported');
      return null;
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('[Punch] Geolocation timeout');
        resolve(null);
      }, 10000); // 10 second timeout

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeout);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (err) => {
          clearTimeout(timeout);
          console.warn('[Punch] Geolocation error:', err.message);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000, // Accept cached position up to 1 minute old
        }
      );
    });
  }, []);

  /**
   * Core punch handler - validates, saves to IndexedDB and triggers sync
   */
  const handlePunch = useCallback(
    async (
      eventType: 'PUNCH_IN' | 'PUNCH_OUT' | 'BREAK_START' | 'BREAK_END',
      options: PunchOptions = {},
      location?: LocationData
    ) => {
      setIsLoading(true);
      setError(null);

      try {
        // A nombre de quien ficha: sin esto la cola no sabía de quién era
        // cada evento y el siguiente en entrar los subía con su token.
        // Además, la validación antifraude necesita SU userId: los checks de
        // histórico (velocidad) miran sólo SUS fichajes, no los del
        // dispositivo (ronda 3, r2b18[4]).
        if (!usuarioActual) {
          throw new Error('No hay sesión: no se puede fichar sin saber quién ficha');
        }

        // 1. Run anti-fraud validation
        const validation = await validatePunch(
          usuarioActual.id,
          location?.latitude,
          location?.longitude,
          location?.accuracy
        );
        setLastValidation(validation);

        // Log validation results
        console.log(`[Punch] Validation result:`, {
          isValid: validation.isValid,
          flags: validation.allFlags,
          warnings: validation.allWarnings,
        });

        // Show warnings to user (but don't block the punch)
        if (validation.allWarnings.length > 0 && validation.isValid) {
          console.warn('[Punch] Warnings:', validation.allWarnings);
        }

        // Block punch if validation failed critically. La velocidad
        // imposible ya NO está entre las duras: viaja como flag
        // IMPOSSIBLE_SPEED con el evento (ronda 3, r2b18[4]) — el fichaje
        // no se descarta por un viaje que quizá hizo otro.
        if (!validation.isValid) {
          const errorMsg = validation.allWarnings.join('. ') || 'Validación fallida';
          setError(errorMsg);
          showToast(errorMsg, 'error');
          throw new Error(errorMsg);
        }

        // 2. Save to IndexedDB (always succeeds if DB is available)
        // Include fraud flags for server-side review
        await createPunchEvent(usuarioActual.id, eventType, {
          breakReason: options.breakReason,
          worksiteUuid: options.worksiteUuid,
          qrToken: options.qrToken,
          workMode: options.workMode,
          latitude: location?.latitude,
          longitude: location?.longitude,
          accuracy: location?.accuracy,
          // Las flags antifraude prometidas por la cabecera de este hook
          // (ronda 3, r2b18[10]): se calculaban y morían en el console.log
          // de arriba. Ahora se guardan con el evento y el sync las sube.
          fraudFlags:
            validation.allFlags.length > 0 ? validation.allFlags : undefined,
        });

        console.log(`[Punch] ${eventType} saved to IndexedDB`);

        // 3. Trigger sync (non-blocking)
        syncService.forceSync().catch((err) => {
          console.warn('[Punch] Background sync failed:', err);
        });

        // 4. Show success feedback (with warning indicator if needed)
        const messages: Record<string, string> = {
          PUNCH_IN: 'Entrada registrada',
          PUNCH_OUT: 'Salida registrada',
          BREAK_START: 'Pausa iniciada',
          BREAK_END: 'Pausa finalizada',
        };

        const successMsg = validation.allWarnings.length > 0
          ? `${messages[eventType]} (con advertencias)`
          : messages[eventType] || 'Registrado';

        showToast(successMsg, validation.allWarnings.length > 0 ? 'info' : 'success');
      } catch (err) {
        console.error(`[Punch] Failed to save ${eventType}:`, err);
        if (!error) {
          setError('Error al registrar fichaje');
          showToast('Error al registrar', 'error');
        }
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [showToast, error]
  );

  /**
   * Bind device to user (call after first login)
   */
  const bindDevice = useCallback(async (userId: number) => {
    try {
      await bindDeviceToUser(userId);
      console.log(`[Punch] Device bound to user ${userId}`);
    } catch (err) {
      console.error('[Punch] Failed to bind device:', err);
    }
  }, []);

  /**
   * Punch IN - Start work day
   */
  const punchIn = useCallback(
    async (location?: LocationData, worksiteUuid?: string, qrToken?: string, workMode: WorkMode = 'ONSITE') => {
      // Optimistic update first. La modalidad viaja con el estado: la salida
      // y las pausas de una jornada REMOTE no deben adquirir GPS (ronda 4,
      // r2b18[1]) y PunchScreen lo decide mirando workState.workMode.
      optimisticPunchIn(workMode);

      try {
        // El centro y la modalidad solo se marcan en la ENTRADA: la salida y
        // las pausas ya ocurren dentro de una jornada abierta, que sabe dónde
        // empezó y en qué modalidad.
        await handlePunch('PUNCH_IN', { worksiteUuid, qrToken, workMode }, location);
      } catch {
        // Rollback optimistic update on failure
        // (refresh from DB to get correct state)
        await useAppStore.getState().refreshWorkStateFromDB();
      }
    },
    [handlePunch, optimisticPunchIn]
  );

  /**
   * Punch OUT - End work day
   */
  const punchOut = useCallback(
    async (location?: LocationData) => {
      optimisticPunchOut();

      try {
        await handlePunch('PUNCH_OUT', {}, location);
      } catch {
        await useAppStore.getState().refreshWorkStateFromDB();
      }
    },
    [handlePunch, optimisticPunchOut]
  );

  /**
   * Start break with reason
   */
  const startBreak = useCallback(
    async (reason: BreakReason, location?: LocationData) => {
      optimisticBreakStart(reason);

      try {
        await handlePunch('BREAK_START', { breakReason: reason }, location);
      } catch {
        await useAppStore.getState().refreshWorkStateFromDB();
      }
    },
    [handlePunch, optimisticBreakStart]
  );

  /**
   * End break
   */
  const endBreak = useCallback(
    async (location?: LocationData) => {
      optimisticBreakEnd();

      try {
        await handlePunch('BREAK_END', {}, location);
      } catch {
        await useAppStore.getState().refreshWorkStateFromDB();
      }
    },
    [handlePunch, optimisticBreakEnd]
  );



  return {
    isLoading,
    error,
    lastValidation,
    punchIn,
    punchOut,
    startBreak,
    endBreak,
    getCurrentLocation,
    bindDevice,
  };
}
